import { z } from "zod";

const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 255;
const PASSWORD_LOWERCASE = /[a-z]/;
const PASSWORD_UPPERCASE = /[A-Z]/;
const PASSWORD_DIGIT = /\d/;
const PASSWORD_SPECIAL = /[\W_]/;

export interface PasswordRule {
  id: string;
  label: string;
  isSatisfied: (password: string) => boolean;
}

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: "min-length",
    label: `Au moins ${MIN_PASSWORD_LENGTH} caractères`,
    isSatisfied: (password) => password.length >= MIN_PASSWORD_LENGTH,
  },
  {
    id: "lowercase",
    label: "Une lettre minuscule (a-z)",
    isSatisfied: (password) => PASSWORD_LOWERCASE.test(password),
  },
  {
    id: "uppercase",
    label: "Une lettre majuscule (A-Z)",
    isSatisfied: (password) => PASSWORD_UPPERCASE.test(password),
  },
  {
    id: "digit",
    label: "Un chiffre (0-9)",
    isSatisfied: (password) => PASSWORD_DIGIT.test(password),
  },
  {
    id: "special",
    label: "Un caractère spécial (!@#$%^&*...)",
    isSatisfied: (password) => PASSWORD_SPECIAL.test(password),
  },
];

const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, `Au moins ${MIN_PASSWORD_LENGTH} caractères`)
  .max(MAX_PASSWORD_LENGTH, `Maximum ${MAX_PASSWORD_LENGTH} caractères`)
  .regex(PASSWORD_LOWERCASE, "Une lettre minuscule requise")
  .regex(PASSWORD_UPPERCASE, "Une lettre majuscule requise")
  .regex(PASSWORD_DIGIT, "Un chiffre requis")
  .regex(PASSWORD_SPECIAL, "Un caractère spécial requis");

export const userFormBaseSchema = z.object({
  username: z
    .string()
    .min(1, "Le nom d'utilisateur est requis")
    .max(255, "Maximum 255 caractères"),
  email: z
    .string()
    .email("Email invalide")
    .min(1, "L'email est requis")
    .max(255, "Maximum 255 caractères"),
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
  profil_id: z.number().int().positive("Le profil est requis"),
  groupe_id: z.number().int().positive("Le groupe est requis"),
  is_active: z.boolean().default(true),
  email_active: z.boolean().default(false),
});

export const userCreateFormSchema = userFormBaseSchema
  .extend({ password: passwordSchema })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
  });

export const userUpdateFormSchema = userFormBaseSchema
  .extend({
    password: passwordSchema.optional().or(z.literal("").optional()),
  })
  .refine(
    (data) =>
      !data.password || data.password.length === 0
        ? true
        : data.password === data.confirmPassword,
    {
      message: "Les mots de passe ne correspondent pas",
      path: ["confirmPassword"],
    }
  );

export type UserFormSchema = z.infer<typeof userCreateFormSchema>;
export type UserUpdateFormSchema = z.infer<typeof userUpdateFormSchema>;
